import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from 'src/components/Banner/Banner'
import { CollectionPage } from 'src/components/Collection/CollectionPage'
import { CollectionSEO } from 'src/components/Collection/CollectionSEO'
import { PostModule } from 'src/components/Post/PostModule'
import { config } from 'src/config'
import { graphQLClient } from 'src/data/graphql/graphql-client'
import { CollectionByHandleDocument, CollectionSlugsDocument } from 'src/data/graphql-generated'
import { fetchCollectionPost } from 'src/data/providers/fetchCollectionPost'
import { fetchProductRatingsBySku } from 'src/data/providers/fetchProductReviews'
import { getCollectionAlternateHandle } from 'src/functions/collection/getCollectionAlternateHandle'
import { getCollectionBreadcrumbs } from 'src/functions/collection/getCollectionBreadcrumbs'
import { getSubcollections } from 'src/functions/collection/getSubcollections'
import { getAlternateLang } from 'src/functions/i18n/getAlternateLang'
import { getLanguageFromLocale, getLocaleFromLanguage } from 'src/functions/i18n/localeFns'
import { pushCategoryImpression } from 'src/functions/util/dataLayer'
import { getMetafield } from 'src/functions/util/getMetafield'
import { useCartQuery } from 'src/hooks/useCartQuery'

import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Collection, CollectionByHandleQuery, CollectionSlugsQuery } from 'src/data/graphql-generated'
import type { Language } from 'src/enum/Language'
import type { Locale } from 'src/enum/Locale'
import type { ShopCanonicals } from 'src/types/Canonical'
import type { CollectionBreadcrumb } from 'src/types/Collection'
import type { Post } from 'src/types/Post'
import type { ProductRating } from 'src/types/Product'

interface ProductCollectionPageProps {
  collection: Collection
  breadcrumbs: CollectionBreadcrumb[]
  post: Post
  ratings: Record<string, ProductRating>
  gearSubcollections: Collection[]
  coffeeSubcollections: Collection[]
  handles: ShopCanonicals
}

export const ProductCollectionPage = ({
  collection,
  breadcrumbs = [],
  post,
  ratings = {},
  gearSubcollections = [],
  coffeeSubcollections = [],
  handles,
}: ProductCollectionPageProps) => {
  const {
    isFetched: cartIsFetched,
    computed: { isLoggedIn },
  } = useCartQuery()

  const { query } = useRouter()
  const { t } = useTranslation()

  useEffect(() => {
    if (cartIsFetched) {
      pushCategoryImpression(collection, isLoggedIn)
    }
  }, [collection, cartIsFetched])

  return (
    <>
      <CollectionSEO collection={collection} handles={handles} />

      {query.referrer === 'coffee-oos' && (
        <Banner headline={t('banner.coffeeNotFound.headline')} subText={t('banner.coffeeNotFound.subText')} />
      )}
      {query.referrer === 'gear-oos' && (
        <Banner headline={t('banner.gearNotFound.headline')} subText={t('banner.gearNotFound.subText')} />
      )}
      {query.referrer === 'gear-portfolio' && (
        <Banner headline={t('banner.gearPortfolio.headline')} subText={t('banner.gearPortfolio.subText')} small />
      )}

      <CollectionPage
        collection={collection}
        breadcrumbs={breadcrumbs}
        ratings={ratings}
        gearSubcollections={gearSubcollections}
        coffeeSubcollections={coffeeSubcollections}
      />

      {post?.modules_and_components.map((module, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <PostModule key={index} module={module} isProductModule={false} />
      ))}
    </>
  )
}

const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params) return { props: {} }
  const slug = String(params.slug)

  const lang = params.lang as Language
  const langCode = lang.toUpperCase()
  const locale = getLocaleFromLanguage(lang)
  const alternateLang = getAlternateLang(lang)

  const { collection }: CollectionByHandleQuery = await graphQLClient.request({
    document: CollectionByHandleDocument,
    variables: { handle: slug, lang: langCode },
  })

  if (!collection) {
    return { notFound: true }
  }

  const post = await fetchCollectionPost(slug, locale)
  const breadcrumbs = await getCollectionBreadcrumbs(collection as Collection, lang)
  const { coffeeSubcollections, gearSubcollections } = await getSubcollections(collection as Collection, lang)

  const handles = {
    [lang]: collection.handle,
    [alternateLang]: await getCollectionAlternateHandle(collection.id, alternateLang),
  }

  const ratingProducts = gearSubcollections.length
    ? gearSubcollections.map((subcollection) => subcollection?.products.nodes).flat()
    : collection.products.nodes

  const productSkuWithVariantSkus = ratingProducts.reduce((acc, product) => {
    const variantSkus = product?.variants.nodes.map((variant) => variant.sku) ?? []
    const [mainVariantSku] = variantSkus

    return {
      ...acc,
      ...(mainVariantSku && {
        [mainVariantSku]: variantSkus,
      }),
    }
  }, {})

  const ratings = await fetchProductRatingsBySku(productSkuWithVariantSkus ?? {})

  return {
    props: { collection, breadcrumbs, post, ratings, gearSubcollections, coffeeSubcollections, handles },
  }
}

const getStaticPaths: GetStaticPaths = async () => {
  const paths: string[] = []
  const langs = config.locales.map((locale) => getLanguageFromLocale(locale as Locale))

  await Promise.all(
    langs.map(async (lang) => {
      const { collections }: CollectionSlugsQuery = await graphQLClient.request({
        document: CollectionSlugsDocument,
        variables: { lang: lang.toUpperCase() },
      })

      collections.nodes.forEach((collection) => {
        if (getMetafield(collection.metafields, 'layout', 'page_type') === 'coffee') {
          paths.push(`/${lang}/c/${collection.handle}`)
        }
      })
    }),
  )

  return {
    paths,
    fallback: false,
  }
}

export { getStaticPaths, getStaticProps }

export default ProductCollectionPage
